import { ActionButton } from "../../components/buttons/ActionButton";
import { HeartIcon } from "@heroicons/react/24/outline";
import { ChartPieIcon, PlayIcon } from "@heroicons/react/20/solid";
import { useRef, useState, useEffect } from "react";
import { useSocket } from "../../contexts/SocketContext";


export const ErgonomicsLivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();

  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  const videoTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null);
  const videoStartTimeRef = useRef(null);
  const videoDurationRef = useRef(0);

  const clearVideoTimeout = () => {
    if (videoTimeoutRef.current) {
      clearTimeout(videoTimeoutRef.current);
      videoTimeoutRef.current = null;
    }
  };

  const clearProgressAnimation = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const startProgressAnimation = () => {
    clearProgressAnimation();

    videoStartTimeRef.current = performance.now();

    const animate = (timestamp) => {
      if (!videoStartTimeRef.current || !videoDurationRef.current) return;

      const elapsed = timestamp - videoStartTimeRef.current;
      const progress = Math.min(elapsed / videoDurationRef.current, 1);

      setVideoProgress(progress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleVideoLoadedMetadata = (e) => {
    clearVideoTimeout();
    clearProgressAnimation();

    const durationSeconds = e.currentTarget?.duration;
    if (!durationSeconds || Number.isNaN(durationSeconds)) return;

    videoDurationRef.current = Math.ceil(durationSeconds * 1000);
    setVideoProgress(0);
    startProgressAnimation();

    videoTimeoutRef.current = setTimeout(() => {
      setIsVideoVisible(false);
      videoTimeoutRef.current = null;
    }, videoDurationRef.current);
  };

  const handleVideoEnded = () => {
    clearVideoTimeout();
    clearProgressAnimation();
    setVideoProgress(0);
    setIsVideoVisible(false);
  };

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      if (data?.bad_posture)
        setIsVideoVisible(data?.bad_posture);
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  useEffect(() => {
    if (!isVideoVisible) {
      clearVideoTimeout();
      clearProgressAnimation();
      setVideoProgress(0);
    }

    return () => {
      clearVideoTimeout();
      clearProgressAnimation();
    };
  }, [isVideoVisible]);

  return (
    <div className="h-full flex flex-col justify-center items-center p-4 gap-4">
      <div className="flex-1 flex flex-col justify-center items-center gap-4 w-full">
        {
          isVideoVisible ?
            <div className="w-full h-full flex justify-center items-center">
              <div className="w-1/3"/>
              <div className="w-1/3 flex justify-center">
                <video
                  src="https://res.cloudinary.com/db0ea7ydl/video/upload/v1772704960/posture_du_paysan_court_1_ttuu0g.mp4"
                  controls={false}
                  autoPlay
                  muted
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onEnded={handleVideoEnded}
                  className="max-w-full h-[700px] object-contain rounded-lg"
                />
              </div>
              <div className="w-1/3 flex justify-center items-center">
                <div className="relative size-56 flex justify-center items-center">
                  <svg className="-rotate-90 size-56" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="44" fill="none" stroke="rgba(44,234,168,0.25)" strokeWidth="8" />
                    <circle cx="60" cy="60" r="44" fill="none" stroke="rgba(44,234,168,1)" strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 44}
                      strokeDashoffset={(2 * Math.PI * 44) * (1 - videoProgress)}
                    />
                  </svg>

                  <div className="absolute inset-0 flex justify-center items-center text-white">
                    <span className="text-5xl">
                      {Math.max(0, Math.ceil((videoDurationRef.current * (1 - videoProgress)) / 1000))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            :
            <>
              <span className='relative flex size-64'>
                <span className="bg-success animate-ping [animation-duration:4s] absolute inline-flex h-full w-full rounded-full opacity-75"/>
                <span className="bg-success relative inline-flex rounded-full size-64 opacity-50"/>
                <div className="bg-perception-gray-800 rounded-full size-48 border-2 border-success absolute top-8 left-8 flex justify-center items-center">
                  <HeartIcon className="size-16 text-success"/>
                </div>
              </span>

              <p className="text-3xl text-perception-gray-700 mt-32">
                Everything is fine.
              </p>
            </>
        }
      </div>

      <div className="flex gap-4">
        <ActionButton color="secondary" text="Show the video" Icon={PlayIcon}
          onClick={() => setIsVideoVisible(1)}
        />
        <ActionButton color="secondary" text="Go to ergonomics dashboard" Icon={ChartPieIcon}
          onClick={() => window.location = '/ergonomics'}
        />
      </div>
    </div>
  );
};